import React, { useMemo, Fragment } from 'react';
import { useAbTestingSdk } from '@sravni/react-utils';
import { initSravniTM } from '@sravni/utils/lib/analytics';
import { DEFAULT_CDN, ANALYTICS_PRECONNECTED_DOMAINS } from '../consts.js';
import { getPreloadedFonts } from '../helpers.js';

const decorateAnalyticsOptions = (options, helpers) => {
    if (!options)
        return undefined;
    const { abTestingSdk } = helpers;
    const abTestingStatistics = abTestingSdk ? abTestingSdk.getAnalyticsStatistics() : undefined;
    return {
        ...options,
        ...(abTestingStatistics ? { abTestingStatistics } : {}),
    };
};
/**
 * @description CommonHead component.
 *
 * @component
 * @example
 * ```jsx
 * <CommonHead>content</CommonHead>
 * ```
 */
const CommonHead = (props) => {
    const { children, cdnUrl = DEFAULT_CDN, preconnectedDomains: preconnectedDomainsOutside = [], preloadedFonts: preloadedFontsOutside = [], analyticsOptions: analyticsOptionsOutside, faviconIco = `${cdnUrl}/shared/static/favicon.ico`, faviconPng = `${cdnUrl}/shared/static/favicon.png`, faviconSvg = `${cdnUrl}/shared/static/favicon.svg`, appleTouchIconSmall = `${cdnUrl}/shared/static/apple-touch-icon-57x57.png`, appleTouchIconMedium = `${cdnUrl}/shared/static/apple-touch-icon-72x72.png`, appleTouchIconLarge = `${cdnUrl}/shared/static/apple-touch-icon-114x114.png`, androidIcon = `${cdnUrl}/shared/static/android-icon-192x192.png`, } = props;
    const abTestingSdk = useAbTestingSdk();
    const analyticsOptions = decorateAnalyticsOptions(analyticsOptionsOutside ?? undefined, {
        abTestingSdk,
    });
    const isAnalyticsEnabled = analyticsOptions && analyticsOptions.enabled;
    const preconnectedDomains = useMemo(() => [
        ...new Set([
            cdnUrl,
            DEFAULT_CDN,
            ...(isAnalyticsEnabled ? ANALYTICS_PRECONNECTED_DOMAINS : []),
            ...preconnectedDomainsOutside,
        ]),
    ], [cdnUrl, isAnalyticsEnabled, preconnectedDomainsOutside]);
    const preloadedFonts = useMemo(() => [...getPreloadedFonts(cdnUrl), ...preloadedFontsOutside], [cdnUrl, preloadedFontsOutside]);
    return (React.createElement(React.Fragment, null,
        React.createElement("meta", { name: "viewport", content: "width=device-width, initial-scale=1, shrink-to-fit=no" }),
        React.createElement("meta", { httpEquiv: "X-UA-Compatible", content: "IE=edge" }),
        React.createElement("meta", { name: "mobile-web-app-capable", content: "yes" }),
        preconnectedDomains.map((domain) => (React.createElement(Fragment, { key: domain },
            React.createElement("link", { rel: "preconnect", href: domain }),
            React.createElement("link", { rel: "dns-prefetch", href: domain })))),
        React.createElement("link", { rel: "manifest", href: `${cdnUrl}/shared/static/manifest.json` }),
        React.createElement("link", { rel: "icon", href: faviconIco }),
        React.createElement("link", { rel: "icon", href: faviconPng, type: "image/png", sizes: "32x32" }),
        React.createElement("link", { rel: "icon", href: faviconSvg, type: "image/svg+xml" }),
        React.createElement("link", { rel: "apple-touch-icon", href: appleTouchIconSmall }),
        React.createElement("link", { rel: "apple-touch-icon", sizes: "72x72", href: appleTouchIconMedium }),
        React.createElement("link", { rel: "apple-touch-icon", sizes: "114x114", href: appleTouchIconLarge }),
        React.createElement("link", { rel: "icon", type: "image/png", sizes: "192x192", href: androidIcon }),
        preloadedFonts.map((font) => (React.createElement("link", { rel: "preload", href: font.url, as: "font", type: font.type, crossOrigin: "anonymous", key: font.url, fetchPriority: "high" }))),
        isAnalyticsEnabled && analyticsOptions && (React.createElement("script", { id: "analytics-initialization", dangerouslySetInnerHTML: { __html: initSravniTM(analyticsOptions) } })),
        children));
};

export { CommonHead };
//# sourceMappingURL=CommonHead.js.map
